import { getDay, format } from 'date-fns';
// DTC-G G3: route the default `checkDate` parameter through the
// canonical clock so callers that omit `checkDate` still get a
// freezable anchor under `clock.setFixed(...)`. Explicit-arg callers
// are unaffected.
import { clock } from './clock';

export interface TechnicianSchedule {
  hours: Record<number, { open: string; close: string }>;
  time_off: string[];
}

/**
 * Checks if a technician is scheduled to work on a given day (not checking current time)
 * Use this for calendar/forecasting logic where you need daily capacity
 * @param schedule - The technician's schedule with hours and time_off
 * @param checkDate - The date to check
 * @returns boolean indicating if the technician is scheduled for that day
 */
export const isTechnicianScheduledForDay = (
  schedule: TechnicianSchedule,
  checkDate: Date
): boolean => {
  if (!schedule?.hours) return false;

  const dayOfWeek = getDay(checkDate);
  const dateStr = format(checkDate, 'yyyy-MM-dd');
  
  // Check if it's a day off
  if (schedule.time_off?.includes(dateStr)) {
    return false;
  }
  
  // Check if they have hours scheduled for this day (handle both numeric and string keys)
  const dayHours = (schedule.hours as any)[dayOfWeek] ?? 
                   (schedule.hours as any)[String(dayOfWeek)];
  if (!dayHours || !dayHours.open || !dayHours.close) {
    return false;
  }
  
  return true;
};

/**
 * Determines if a technician is present RIGHT NOW based on their schedule
 * Use this for live/real-time presence checks (e.g., dayboard)
 * @param schedule - The technician's schedule with hours and time_off
 * @param checkDate - The date to check (defaults to now)
 * @returns boolean indicating if the technician should be present
 */
export const isTechnicianScheduledPresent = (
  schedule: TechnicianSchedule,
  checkDate: Date = clock.now()
): boolean => {
  if (!schedule?.hours) return false;

  const dayOfWeek = getDay(checkDate);
  const dateStr = format(checkDate, 'yyyy-MM-dd'); // LOCAL format, not UTC
  
  // Check if it's a day off
  if (schedule.time_off?.includes(dateStr)) {
    return false;
  }
  
  // Check if they have hours scheduled for this day (handle both numeric and string keys)
  const dayHours = (schedule.hours as any)[dayOfWeek] ?? 
                   (schedule.hours as any)[String(dayOfWeek)];
  if (!dayHours || !dayHours.open || !dayHours.close) {
    return false;
  }
  
  // P5B-D round 1 — DST-safe wall-clock comparison.
  //
  // Previous implementation used `parse(dayHours.open, 'HH:mm', checkDate)`
  // + `isWithinInterval(...)`, which constructs Date objects from the
  // `'HH:mm'` strings against `checkDate` in the JS runtime's local
  // timezone. On DST transition days (spring-forward / fall-back), this
  // can yield inconsistent or impossible local times when the parsed
  // open/close instants cross the transition boundary, breaking the
  // wall-clock-window semantics the function is supposed to encode.
  //
  // Minutes-of-day approach below compares wall-clock-minutes
  // numerically and never builds Date objects from the open/close
  // strings — DST is a non-issue: 09:00 is always 540 minutes after
  // midnight regardless of whether the local day has 23, 24, or 25
  // hours.
  //
  // Implicit timezone contract (unchanged from pre-fix behavior):
  // `checkDate.getHours() / getMinutes()` return values in the JS
  // runtime's local timezone; callers are responsible for ensuring
  // `checkDate` is interpreted in the same wall-clock as
  // `dayHours.open` / `dayHours.close` (typically the shop's local
  // timezone). Adding an explicit `shopTimezone` parameter is a
  // separate, larger refactor (3 production call sites would need
  // shop context plumbed through) and is not required to fix the DST
  // bug — the minutes-of-day approach is DST-safe under the existing
  // implicit-timezone contract.
  try {
    const checkMinutes = checkDate.getHours() * 60 + checkDate.getMinutes();
    const [openH, openM] = dayHours.open.split(':').map(Number);
    const [closeH, closeM] = dayHours.close.split(':').map(Number);
    if (
      !Number.isFinite(openH) || !Number.isFinite(openM) ||
      !Number.isFinite(closeH) || !Number.isFinite(closeM)
    ) {
      return false;
    }
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    return checkMinutes >= openMinutes && checkMinutes <= closeMinutes;
  } catch {
    return false;
  }
};
